import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, getDocs, query, where, addDoc, updateDoc } from 'firebase/firestore';
import { setKey, fromAddress } from 'react-geocode';
import { durationGetter } from '../functions/classes';
import { AlertStack } from '../functions/ui';
import { ConfirmationModal } from '../functions/ui';
import { Divider } from 'antd';
import '../webapp.css';

//for long and lat derivation - this is fine
setKey('AIzaSyAVLX9CMDEXnWN9ZtolQ67h_xmhJKhT-vw'); 

const FORM_STORAGE_KEY = 'shiftcall_form_state';

const ShiftCall = () => {
  const auth = getAuth();
  const db = getFirestore();

  const [staffType, setStaffType] = useState('');
  const [skill, setSkill] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [notes, setNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [alerts, setAlerts] = useState([]);

  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);

  // State for individual field errors
  const [staffTypeError, setStaffTypeError] = useState('');
  const [skillError, setSkillError] = useState('');
  const [streetNumberError, setStreetNumberError] = useState('');
  const [streetNameError, setStreetNameError] = useState('');
  const [cityError, setCityError] = useState('');
  const [provinceError, setProvinceError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');


  // Helper to build the full address string
  const getFullAddress = () => `${streetNumber} ${streetName}, ${city}, ${province}, ${country}, ${postalCode}`;

  const prepareForSubmission = async () => {
    setIsProcessing(true);

    if (!validateInputs()) {
      setIsProcessing(false);
      return;
    }

    const address = getFullAddress();
    const duration = calculateDuration();
    setConfirmationMessage(
      `Requesting coverage for ${address} by a ${staffType} for ${duration}?`
    );
    setShowConfirmationAlert(true);
    setIsProcessing(false);
  };

  const calculateDuration = () => {
    const { days, hours, minutes } = durationGetter(startDate, endDate);
    const durationComponents = [];
    if (days > 0) durationComponents.push(`${days} day${days === 1 ? '' : 's'}`);
    if (hours > 0) durationComponents.push(`${hours} hr${hours === 1 ? '' : 's'}`);
    if (minutes > 0) durationComponents.push(`${minutes} min${minutes === 1 ? '' : 's'}`);
    return durationComponents.join(' ');
  };

  const submitShift = async () => {
    setIsProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        showAlert('Error', 'User not logged in', 'error');
        setIsProcessing(false);
        return;
      }

      // Use the signed-in user's ID as companyID
      const companyID = user.uid;

      const address = getFullAddress();
      const coordinates = await fetchCoordinates(address);
      if (!coordinates) {
        setIsProcessing(false);
        return;
      }

      // Calculate duration fields
      const { days, hours, minutes } = durationGetter(startDate, endDate);

      const shiftData = {
        staffType,
        skill,
        streetNumber,
        streetName,
        city,
        province,
        country,
        postalCode,
        notes,
        startDate,
        endDate,
        days,
        hours,
        minutes,
        latitude: coordinates.latitude, // Save latitude
        longitude: coordinates.longitude, // Save longitude
        dateCreated: new Date().toISOString(),
        companyID,
        status: 'pending'
      };

      const shiftRef = doc(collection(db, 'shifts'));
      //save
      await setDoc(shiftRef, shiftData);

      // attached companyID to the vercel API call and data transfe
      await fetch('https://capitalbeaverserver.vercel.app/api/sendnotification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyID, title: 'New Shift Available', excludeOnShift: true }),
      });

      // Save notification to each staff user's notifications subcollection
      // 1. Get all staff users for this company who are not currently on shift
      const staffQuery = query(
        collection(db, 'users'),
        where('employer', 'array-contains', companyID),
        where('onShift', '==', false) // Exclude users who are on shift
      );
      const staffSnapshot = await getDocs(staffQuery);
      const notificationData = {
        message: `Your employer just posted a new shift. View in home page`,
        timeposted: new Date().toISOString(),
      };
      for (const staffDoc of staffSnapshot.docs) {
        const notificationsCol = collection(db, 'users', staffDoc.id, 'notifications');
        await addDoc(notificationsCol, notificationData);
        // Also set read: false on the staff user's main document
        await updateDoc(doc(db, 'users', staffDoc.id), { read: false });
      }

      showAlert('Your new shift has been sent. All frontline staff are notified', 'success');    
      resetForm();
      // Clear persisted form state after successful submit
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (error) {
      showAlert('Error', 'Failed to submit', 'error');
    } finally {
      setIsProcessing(false);
      setShowConfirmationAlert(false);
    }
  };


  const validateInputs = () => {
    // Clear previous errors
    setStaffTypeError('');
    setSkillError('');
    setStreetNumberError('');
    setStreetNameError('');
    setCityError('');
    setProvinceError('');
    setCountryError('');
    setPostalCodeError('');
    setStartDateError('');
    setEndDateError('');

    let isValid = true;
    const fields = [
      { value: staffType, setError: setStaffTypeError, msg: 'Staff type is required' },
      { value: skill, setError: setSkillError, msg: 'Skill is required' },
      { value: streetNumber, setError: setStreetNumberError, msg: 'Street number is required' },
      { value: streetName, setError: setStreetNameError, msg: 'Street name is required' },
      { value: city, setError: setCityError, msg: 'City is required' },
      { value: province, setError: setProvinceError, msg: 'Province is required' },
      { value: country, setError: setCountryError, msg: 'Country is required' },
      { value: postalCode, setError: setPostalCodeError, msg: 'Postal code is required' },
      { value: startDate, setError: setStartDateError, msg: 'Start date is required' },
      { value: endDate, setError: setEndDateError, msg: 'End date is required' },
    ];

    fields.forEach(({ value, setError, msg }, idx) => {
      if (!value) {
        setError(msg);
        isValid = false;
      }
    });

    // Special validation for streetNumber
    if (streetNumber && isNaN(streetNumber)) {
      setStreetNumberError('Street number must be numeric');
      isValid = false;
    }

    // Postal code length validation
    if (postalCode && postalCode.length < 6) {
      setPostalCodeError('Postal code must be at least 6 characters');
      isValid = false;
    }

    // Date logic
    const now = new Date();
    if (startDate) {
      const start = new Date(startDate);
      if (start < now) {
        setStartDateError('Start date cannot be in the past');
        isValid = false;
      }
    }
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      setEndDateError('End date must be after start date');
      isValid = false;
    }

    return isValid;
  };

  const fetchCoordinates = async (address) => {
    try {
      const response = await fromAddress(address);
      if (!response.results || response.results.length === 0) {
        showAlert('Failed to submit: address could not be geocoded', 'error');
        return null;
      }
      const location = response.results[0].geometry.location;
      if (location.lat === 0 && location.lng === 0) {
        showAlert('Failed to submit: address could not be geocoded', 'error');
        return null;
      }
      return { latitude: location.lat, longitude: location.lng };
    } catch (error) {
      showAlert('Failed to submit: address could not be geocoded', 'error');
      return null;
    }
  };

  const resetForm = () => {
    setStaffType('');
    setSkill('');
    setStreetNumber('');
    setStreetName('');
    setCity('');
    setProvince('');
    setCountry('');
    setPostalCode('');
    setNotes('');
    setStartDate('');
    setEndDate('');
    setStaffTypeError('');
    setSkillError('');
    setStreetNumberError('');
    setStreetNameError('');
    setCityError('');
    setProvinceError('');
    setCountryError('');
    setPostalCodeError('');
    setStartDateError('');
    setEndDateError('');
    // Clear persisted form state
    localStorage.removeItem(FORM_STORAGE_KEY);
  };

  const showAlert = (message, type) => {
    setAlerts(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        message, // Only show the message, not the title
        type,
      },
    ]);
  };

  // Restore form state from localStorage on mount (only if fields are empty)
  useEffect(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Only restore if all fields are empty (prevents overwriting in-progress edits)
        if (
          !staffType && !skill && !streetNumber && !streetName && !city && !province && !country && !postalCode && !notes && !startDate && !endDate
        ) {
          setStaffType(data.staffType || '');
          setSkill(data.skill || '');
          setStreetNumber(data.streetNumber || '');
          setStreetName(data.streetName || '');
          setCity(data.city || '');
          setProvince(data.province || '');
          setCountry(data.country || '');
          setPostalCode(data.postalCode || '');
          setNotes(data.notes || '');
          setStartDate(data.startDate || '');
          setEndDate(data.endDate || '');
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save form state to localStorage on any change (debounced for performance)
  useEffect(() => {
    const timeout = setTimeout(() => {
      const data = {
        staffType,
        skill,
        streetNumber,
        streetName,
        city,
        province,
        country,
        postalCode,
        notes,
        startDate,
        endDate,
      };
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data));
    }, 200);
    return () => clearTimeout(timeout);
  }, [staffType, skill, streetNumber, streetName, city, province, country, postalCode, notes, startDate, endDate]);

  useEffect(() => {
    if (showConfirmationAlert) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Clean up in case component unmounts while modal is open
    return () => {
      document.body.style.overflow = '';
    };
  }, [showConfirmationAlert]);

  return (
    <div className='dashboard-box shiftcall'> 
        <h2>Shift Call</h2>
        <p className='small-text'>Send out on-demand shift coverage requests</p>
        <Divider className='dash-divider'/>
        <div className="c30section">
          <div className="r15section">
            <div className="c15section">
              <div className="small-text">Staff type</div>
              <input
                type="text"
                value={staffType}
                onChange={(e) => setStaffType(e.target.value)}
                onFocus={() => setStaffTypeError('')}
              />
              {staffTypeError && <p className="error-message">{staffTypeError}</p>}
            </div>
            <div className="c15section">
              <div className="small-text">Skill</div>
              <input
                type="text"
                value={skill}
                onChange={(e) => setSkill(e.target.value)}
                onFocus={() => setSkillError('')}
              />
              {skillError && <p className="error-message">{skillError}</p>}
            </div>
          </div>
          <div className="r15section">
            <div className="c15section">
              <div className="small-text">Start</div>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onFocus={() => setStartDateError('')}
              />
              {startDateError && <p className="error-message">{startDateError}</p>}
            </div>
            <div className="c15section">
              <div className="small-text">End</div>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onFocus={() => setEndDateError('')}
              />
              {endDateError && <p className="error-message">{endDateError}</p>}
            </div>
          </div>
          <div className="r15section">
            <div className="c15section">
              <div className="small-text">Street number</div>
              <input
                type="text"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                onFocus={() => setStreetNumberError('')}
              />
              {streetNumberError && <p className="error-message">{streetNumberError}</p>}
            </div>
            <div className="c15section">
              <div className="small-text">Street name</div>
              <input
                type="text"
                value={streetName}
                onChange={(e) => setStreetName(e.target.value)}
                onFocus={() => setStreetNameError('')}
              />
              {streetNameError && <p className="error-message">{streetNameError}</p>}
            </div>
            <div className="c15section">
              <div className="small-text">City</div>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onFocus={() => setCityError('')}
              />
              {cityError && <p className="error-message">{cityError}</p>}
            </div>
          </div>
          <div className="r15section">
            <div className="c15section">
              <div className="small-text">Province</div>
              <input
                type="text"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                onFocus={() => setProvinceError('')}
              />
              {provinceError && <p className="error-message">{provinceError}</p>}
            </div>
            <div className="c15section">
              <div className="small-text">Country</div>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                onFocus={() => setCountryError('')}
              />
              {countryError && <p className="error-message">{countryError}</p>}
            </div>
            <div className="c15section">
              <div className="small-text">Postal code</div>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                onFocus={() => setPostalCodeError('')}
              />
              {postalCodeError && <p className="error-message">{postalCodeError}</p>}
            </div>
          </div>
          <div className="c15section">
            <div className="small-text">Extra information</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {/* No notesError validation shown, only firebase/general errors below */}
            {alerts.length > 0 && (
              <AlertStack
                alerts={alerts}
                onAlertClose={id => setAlerts(alerts => alerts.filter(a => a.id !== id))}
              />
            )}
          </div>
          {isProcessing ? (
            <div className="loading">Processing...</div>
          ) : (
            <>
              <button className="submit-button full-button" onClick={prepareForSubmission}>
                Submit
              </button>

            </>
          )}
        </div>

        {showConfirmationAlert && (
          <ConfirmationModal
            open={showConfirmationAlert}
            title="Are you sure?"
            message={confirmationMessage}
            confirmText="Submit"
            cancelText="Cancel"
            onConfirm={submitShift}
            onCancel={() => setShowConfirmationAlert(false)}
            isSubmitting={isProcessing}
          />
        )}
    </div>
  );
};

export default ShiftCall;